/**
 * Design va rating rejimlarining yechuvchilari (tarkiblar, D, W, bosqichlar).
 *
 * design: xD, xW, R → D, W (balans); Rmin (pinch); Nmin (Fenske); R > Rmin → zinapoya.
 * rating: N, NF, R va D/F (yoki xD) → noma'lum skalyar u (xD yoki D/F) bisection bilan:
 *         f(u) = N_req(u) − N,  N_req — qat'iy NF bilan zinapoya (kasr bosqichlar, MAX_STAGES
 *         bilan cheklangan — pinch bo'lsa ham f chekli qoladi).
 *         f(hi) ≤ 0 → bosqichlar ortiqcha (column.overdesigned), u = hi;
 *         f(lo) ≥ 0 → hatto minimal ajratish ham bo'lmaydi (column.unattainablePurity).
 *
 * Ogohlantirish kodlari steady.ts boshida ro'yxatlangan.
 */
import { bisection } from '../../solvers';
import { warn, type Warning } from '../../types';
import {
  fenskeMinimumStages,
  minimumReflux,
  operatingLines,
  stepStages,
  type OperatingLines,
  type SteppingResult,
} from './mccabe-thiele';
import type { RectificationInputs, RectificationParams } from './types';
import type { BinaryVle } from './vle';

/** Zinapoyada maksimal bosqichlar — bundan ko'p bo'lsa pinch deb qabul qilinadi */
export const MAX_STAGES = 500;
const EPS = 1e-9;
const round3 = (v: number): number => Number(v.toFixed(3));

export interface Counted {
  lines: OperatingLines;
  stepping: SteppingResult | null;
  Rmin: number;
  stagesExact: number;
}

/** Bir tarkib juftligi uchun zinapoya (R ≤ Rmin bo'lsa Infinity) */
export function countStages(
  vle: BinaryVle,
  R: number,
  q: number,
  xF: number,
  xD: number,
  xW: number,
  feedStage?: number,
): Counted {
  const lines = operatingLines(R, q, xD, xW, xF);
  const Rmin = minimumReflux(vle, xD, xF, q);
  if (!(R > Rmin)) return { lines, stepping: null, Rmin, stagesExact: Infinity };
  const opts =
    feedStage === undefined ? { maxStages: MAX_STAGES } : { maxStages: MAX_STAGES, feedStage };
  const stepping = stepStages(vle, lines, opts);
  return { lines, stepping, Rmin, stagesExact: stepping.stagesExact };
}

/** Fenske uchun o'rtacha α: Raoult — geometrik o'rtacha tepa va tub */
export function meanAlpha(vle: BinaryVle, xD: number, xW: number): number {
  if (vle.mode === 'constant-alpha') return vle.alpha(0.5);
  return Math.sqrt(vle.alpha(xD) * vle.alpha(xW));
}

export interface Solved {
  xD: number;
  xW: number;
  D: number;
  W: number;
  counted: Counted;
  converged: boolean;
  iterations: number;
  residual: number;
  message?: string;
}

function failed(
  base: Omit<Solved, 'counted' | 'converged' | 'iterations' | 'residual' | 'message'>,
  counted: Counted,
  stagesExact: number,
  message: string,
): Solved {
  return {
    ...base,
    counted: { ...counted, stepping: null, stagesExact },
    converged: false,
    iterations: 0,
    residual: NaN,
    message,
  };
}

export function solveDesign(
  vle: BinaryVle,
  i: RectificationInputs,
  q: number,
  warnings: Warning[],
): Solved {
  const xD = i.xD as number;
  const xW = i.xW as number;
  const F = i.feedFlow;
  const { xF, refluxRatio: R } = i;
  const counted = countStages(vle, R, q, xF, xD, xW);
  if (xD <= xF || xW >= xF) {
    warn(warnings, 'column.specInfeasible', 'critical', { xF, xD, xW });
    const D = Math.min(F, Math.max(0, (F * (xF - xW)) / (xD - xW)));
    return failed({ xD, xW, D, W: F - D }, counted, NaN, 'column.specInfeasible');
  }
  const D = (F * (xF - xW)) / (xD - xW);
  const W = F - D;
  const alpha = meanAlpha(vle, xD, xW);
  const Nmin = fenskeMinimumStages(alpha, xD, xW);
  if (!(Nmin < MAX_STAGES)) {
    warn(warnings, 'column.unattainablePurity', 'warning', {
      alpha: round3(alpha),
      Nmin: round3(Nmin),
    });
    return failed({ xD, xW, D, W }, counted, Infinity, 'column.unattainablePurity');
  }
  if (counted.stepping === null || counted.stepping.pinched) {
    warn(warnings, 'column.belowMinimumReflux', 'critical', {
      R: round3(R),
      Rmin: round3(counted.Rmin),
    });
    return failed({ xD, xW, D, W }, counted, Infinity, 'column.belowMinimumReflux');
  }
  return {
    xD,
    xW,
    D,
    W,
    counted,
    converged: true,
    iterations: Math.ceil(counted.stagesExact),
    residual: 0,
  };
}

interface Compositions {
  xD: number;
  xW: number;
  D: number;
}

export function solveRating(
  vle: BinaryVle,
  p: RectificationParams,
  i: RectificationInputs,
  q: number,
  warnings: Warning[],
): Solved {
  const N = p.stages as number;
  const NF = p.feedStage as number;
  const F = i.feedFlow;
  const { xF, refluxRatio: R } = i;
  const fail = (xD: number, xW: number, message: string, stagesExact: number): Solved =>
    failed({ xD, xW, D: 0, W: F }, countStages(vle, R, q, xF, xD, xW, NF), stagesExact, message);

  // Noma'lum skalyar u ∈ (lo, hi) → (xD, xW, D)
  let lo: number;
  let hi: number;
  let compositions: (u: number) => Compositions;
  if (i.distillateFraction !== undefined) {
    const d = i.distillateFraction;
    lo = xF + EPS;
    hi = Math.min(1 - EPS, xF / d - EPS);
    compositions = (xD) => ({ xD, xW: (xF - d * xD) / (1 - d), D: d * F });
  } else {
    const xD = i.xD as number;
    if (xD <= xF) {
      warn(warnings, 'column.specInfeasible', 'critical', { xF, xD });
      return fail(xD, xF, 'column.specInfeasible', NaN);
    }
    const Rmin = minimumReflux(vle, xD, xF, q);
    if (!(R > Rmin)) {
      warn(warnings, 'column.belowMinimumReflux', 'critical', { R: round3(R), Rmin: round3(Rmin) });
      return fail(xD, xF, 'column.belowMinimumReflux', Infinity);
    }
    lo = EPS;
    hi = xF / xD - EPS;
    compositions = (d) => ({ xD, xW: (xF - d * xD) / (1 - d), D: d * F });
  }
  if (!(hi > lo)) {
    warn(warnings, 'column.specInfeasible', 'critical', { xF });
    const c = compositions(lo);
    return fail(c.xD, c.xW, 'column.specInfeasible', NaN);
  }

  const f = (u: number): number => {
    const c = compositions(u);
    return Math.min(countStages(vle, R, q, xF, c.xD, c.xW, NF).stagesExact, MAX_STAGES) - N;
  };
  const fHi = f(hi);
  let u: number;
  let converged = true;
  let iterations = 1;
  let residual = 0;
  let message: string | undefined;
  if (fHi <= 0) {
    warn(warnings, 'column.overdesigned', 'info', { stages: N });
    u = hi;
    residual = Math.abs(fHi);
  } else {
    if (f(lo) >= 0) {
      const c = compositions(lo);
      warn(warnings, 'column.unattainablePurity', 'warning', { stages: N });
      return fail(c.xD, c.xW, 'column.unattainablePurity', Infinity);
    }
    const r = bisection(f, lo, hi, { xtol: 1e-11, ftol: 1e-9 });
    u = r.value;
    converged = r.converged;
    iterations = r.iterations;
    residual = r.residual;
    if (!r.converged) message = r.message;
  }
  const c = compositions(u);
  const counted = countStages(vle, R, q, xF, c.xD, c.xW, NF);
  if (counted.stepping && !counted.stepping.feedStageOptimal)
    warn(warnings, 'column.feedStageSuboptimal', 'info', { feedStage: NF });
  const solved: Solved = {
    xD: c.xD,
    xW: c.xW,
    D: c.D,
    W: F - c.D,
    counted,
    converged,
    iterations,
    residual,
  };
  if (message !== undefined) solved.message = message;
  return solved;
}
